import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Sparkles, Check, Shield, Leaf, FlaskConical, UserCog, Bot, User, LogOut, ChevronDown, Loader2, Home, Scan, PawPrint, UserCircle, Heart, Fish, ChefHat, ArrowRight, Menu, X, UserPlus } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

import '@/App.css';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { BoxProvider, useBox } from './context/BoxContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProductCard } from './components/ProductCard';
import { FilterBar } from './components/FilterBar';
import { BoxVisualizer } from './components/BoxVisualizer';
import { CheckoutModal } from './components/CheckoutModal';
import { MobileBottomBar } from './components/MobileBottomBar';
import { BoxDrawer } from './components/BoxDrawer';
import { AuthModal } from './components/AuthModal';
import { TedAI } from './pages/TedAI';
import { TikTokLive } from './pages/TikTokLive';
import { PublicVsPage } from './pages/PublicVsPage';
import { TedsTablePage, TedsTableSuccess } from './pages/TedsTablePage';
import { DogMatch } from './pages/DogMatch';
import { AccountPage } from './pages/AccountPage';
import { SignUpPage } from './pages/SignUpPage';
import { AdminLogin } from './pages/AdminLogin';
import { AdminDashboard } from './pages/AdminDashboard';
import { HomePage } from './pages/HomePage';
import { FFOSSignUpPage } from './pages/FFOSSignUpPage';
import { SampleBoxHero } from './components/home/SampleBoxHero';
import { TrustSection } from './components/home/TrustSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


const UserMenu = ({ onNavigateAccount }) => {
  const { customer, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  if (!customer) {
    return (
      <>
        <button
          onClick={() => setShowAuth(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#0FA49C] text-white hover:bg-[#0d8f88] transition-colors"
          data-testid="login-btn"
        >
          <User className="w-4 h-4" />
          Sign In
        </button>
        <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      </>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors"
        data-testid="user-menu-btn"
      >
        {customer.picture ? (
          <img src={customer.picture} alt="" className="w-7 h-7 rounded-full" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#0FA49C] text-white flex items-center justify-center text-xs font-bold">
            {customer.name?.[0]?.toUpperCase() || '?'}
          </div>
        )}
        <span className="hidden sm:inline text-[#222] max-w-[120px] truncate">{customer.name}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#999]" />
      </button>

      {showMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50" data-testid="user-dropdown">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-[#222] truncate">{customer.name}</p>
              <p className="text-xs text-[#999] truncate">{customer.email}</p>
            </div>
            <button
              onClick={() => { setShowMenu(false); onNavigateAccount(); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#444] hover:bg-gray-50 transition-colors"
              data-testid="my-account-btn"
            >
              <User className="w-4 h-4" />
              My Account
            </button>
            <button
              onClick={() => { setShowMenu(false); logout(); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const AuthCallback = () => {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const processedRef = React.useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (match) {
      const sessionId = match[1];

      // If this Google signup came from /ffos, we stashed the activity
      // selections in sessionStorage before redirecting. Pull them back and
      // forward them to /auth/google so the new customer still gets the
      // right source + tags.
      let pendingSource = null;
      let pendingTags = null;
      try {
        const raw = sessionStorage.getItem('pending_ffos_signup');
        if (raw) {
          const parsed = JSON.parse(raw);
          pendingSource = parsed.source || null;
          pendingTags = Array.isArray(parsed.tags) ? parsed.tags : null;
          sessionStorage.removeItem('pending_ffos_signup');
        }
      } catch { /* ignore */ }

      googleLogin(sessionId, pendingSource, pendingTags)
        .then(() => {
          toast.success('Signed in with Google!');
          // If this was a /ffos passport signup, land them back on the
          // thank-you panel instead of the generic home page.
          const dest = pendingSource === 'ffos' ? '/ffos?welcome=1' : '/';
          navigate(dest, { replace: true });
        })
        .catch((err) => {
          toast.error('Google sign-in failed. Please try again.');
          navigate('/', { replace: true });
        });
    } else {
      navigate('/', { replace: true });
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA49C]" />
    </div>
  );
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = window.location.hash.match(/reset_token=([^&]+)/)?.[1] || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Reset failed. The link may have expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm overflow-hidden">
        <div className="bg-[#0FA49C] p-5 text-white text-center">
          <h2 className="font-bold text-lg">{success ? 'Password Reset!' : 'Set New Password'}</h2>
        </div>
        <div className="p-5">
          {success ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm text-[#444] mb-4">Your password has been reset successfully.</p>
              <Button
                onClick={() => navigate('/', { replace: true })}
                className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl px-6 py-4 font-bold text-sm"
                data-testid="back-home-btn"
              >
                Go to Homepage & Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-[#666] mb-1">New Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#666] mb-1">Confirm Password</label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  data-testid="reset-confirm-input"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl py-5 font-bold"
                data-testid="reset-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};


const MainContent = ({ initialTab = 'home' }) => {
  const [products, setProducts] = useState([]);
  const [boxSizes, setBoxSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [tedAiEnabled, setTedAiEnabled] = useState(false);
  const [tikTokLiveEnabled, setTikTokLiveEnabled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: null,
    lifestage: null,
    breed_size: null,
    benefits: null,
  });
  const [showCheckout, setShowCheckout] = useState(false);
  const [showBoxDrawer, setShowBoxDrawer] = useState(false);

  const { isFull } = useBox();
  const { customer } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // If user landed on /signup via the Ted's Table promo banner, redirect them
  // back to the menu with a promo code after successful signup.
  const cameFromTedsTable = new URLSearchParams(location.search).get('from') === 'teds-table';

  // Per-tab document title + meta description for SEO.
  useEffect(() => {
    const titles = {
      home: {
        title: "Ultimate Pet Foods — Natural UK Dog Food + Ted AI Bag Scanner",
        description: "Build a custom sample box, scan any dog food bag with Ted AI, and get your dog matched with the right food. The UK's first AI-powered dog food service.",
      },
      'sample-box': {
        title: "Build Your Sample Box — Ultimate Pet Foods",
        description: "Pick your natural dog food sample box. Ted picks the perfect trial size for your dog. Free UK delivery on selected boxes.",
      },
      'ted-ai': {
        title: "Ted AI — Scan Any Dog Food Bag & See What's Really Inside",
        description: "Upload a photo of any dog food bag and Ted AI tells you the true ingredients, quality score, and whether it's right for your dog.",
      },
      'get-matched': {
        title: "Get Your Dog Matched — AI Dog Food Recommendation | Ultimate Pet Foods",
        description: "Tell us about your dog and our AI recommends the perfect natural food. Free, no signup needed.",
      },
      'tiktok-live': {
        title: "TikTok Live Bag Match — Ultimate Pet Foods",
        description: "Submit your dog food bag for a live TikTok comparison with Ted.",
      },
      account: {
        title: "My Account — Ultimate Pet Foods",
        description: "Manage your dog profiles, order history, and price alerts.",
      },
      signup: {
        title: "Create Account — Ultimate Pet Foods",
        description: "Sign up to save your dog's profile, track orders, and get alerts when your favourite dog food goes on sale.",
      },
    };
    const t = titles[activeTab] || titles.home;
    document.title = t.title;
    const metaEl = document.querySelector('meta[name="description"]');
    if (metaEl) metaEl.setAttribute('content', t.description);
  }, [activeTab]);

  useEffect(() => {
    fetchProducts();
    fetchBoxSizes();
    fetchTedAiSetting();
    fetchTikTokLiveSetting();
    trackPageView();
    
    // Track page view every 2 minutes to keep user active
    const interval = setInterval(trackPageView, 120000);
    return () => clearInterval(interval);
  }, []);

  const trackPageView = async () => {
    try {
      // Generate or get session ID
      let sessionId = localStorage.getItem('session_id');
      if (!sessionId) {
        sessionId = 'session_' + Math.random().toString(36).substring(2, 15);
        localStorage.setItem('session_id', sessionId);
      }

      await axios.post(`${API}/track-visit`, {
        session_id: sessionId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking visit:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchBoxSizes = async () => {
    try {
      const response = await axios.get(`${API}/box-sizes`);
      setBoxSizes(response.data);
    } catch (error) {
      console.error('Error fetching box sizes:', error);
    }
  };

  const fetchTedAiSetting = async () => {
    try {
      const response = await axios.get(`${API}/settings/ted-ai-enabled`);
      setTedAiEnabled(response.data.enabled);
    } catch (error) {
      console.error('Error fetching Ted AI setting:', error);
      setTedAiEnabled(true); // default to on if setting fails
    }
  };

  const fetchTikTokLiveSetting = async () => {
    try {
      const response = await axios.get(`${API}/settings/tiktok-live-enabled`);
      setTikTokLiveEnabled(response.data.enabled);
    } catch (error) {
      console.error('Error fetching TikTok Live setting:', error);
      setTikTokLiveEnabled(false);
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      if (filters.type && product.type !== filters.type) return false;
      if (filters.lifestage && product.lifestage !== filters.lifestage) return false;
      if (filters.breed_size && product.breed_size !== filters.breed_size && product.breed_size !== 'all') return false;
      
      // Filter by benefits (tags)
      if (filters.benefits) {
        if (filters.benefits === 'meat_based') {
          // Meat based: NOT fish_based
          const isFishBased = product.tags && product.tags.includes('fish_based');
          if (isFishBased) return false;
        } else {
          // For other benefits, check if the tag exists in product tags
          const hasBenefit = product.tags && product.tags.includes(filters.benefits);
          if (!hasBenefit) return false;
        }
      }
      
      return true;
    });
  }, [products, filters]);

  const handleCheckout = () => {
    if (isFull) {
      setShowCheckout(true);
      setShowBoxDrawer(false);
    }
  };

  return (
    <>
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50" data-testid="top-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-6 min-w-0">
            <img
              src="https://customer-assets.emergentagent.com/job_pet-sample-box/artifacts/c7mt7a10_Ultimate%20Pet%20Food%20Logo%20splash.jpg"
              alt="Ultimate Pet Foods"
              className="h-8 w-auto my-3 cursor-pointer flex-shrink-0"
              onClick={() => { setActiveTab('home'); setMobileMenuOpen(false); window.scrollTo(0, 0); }}
            />
            {/* Desktop tabs */}
            <div className="hidden md:flex gap-1" data-testid="main-tabs">
              <button
                onClick={() => setActiveTab('home')}
                className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === 'home'
                    ? 'border-[#0FA49C] text-[#0FA49C]'
                    : 'border-transparent text-[#666] hover:text-[#222]'
                }`}
                data-testid="tab-home"
              >
                <Home className="w-4 h-4" />
                Home
              </button>
              <button
                onClick={() => setActiveTab('sample-box')}
                className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === 'sample-box'
                    ? 'border-[#0FA49C] text-[#0FA49C]'
                    : 'border-transparent text-[#666] hover:text-[#222]'
                }`}
                data-testid="tab-sample-box"
              >
                <Package className="w-4 h-4" />
                Sample Box
              </button>
              <button
                onClick={() => setActiveTab('ted-ai')}
                className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === 'ted-ai'
                    ? 'border-[#0FA49C] text-[#0FA49C]'
                    : 'border-transparent text-[#666] hover:text-[#222]'
                }`}
                data-testid="tab-ted-ai"
                style={{ display: tedAiEnabled ? 'flex' : 'none' }}
              >
                <Bot className="w-4 h-4" />
                Ted AI
              </button>
              <button
                onClick={() => setActiveTab('get-matched')}
                className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === 'get-matched'
                    ? 'border-[#0FA49C] text-[#0FA49C]'
                    : 'border-transparent text-[#666] hover:text-[#222]'
                }`}
                data-testid="tab-get-matched"
              >
                <PawPrint className="w-4 h-4" />
                Dog Match
              </button>
              <button
                onClick={() => setActiveTab('tiktok-live')}
                className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === 'tiktok-live'
                    ? 'border-[#0FA49C] text-[#0FA49C]'
                    : 'border-transparent text-[#666] hover:text-[#222]'
                }`}
                data-testid="tab-tiktok-live"
                style={{ display: tikTokLiveEnabled ? 'flex' : 'none' }}
              >
                <Sparkles className="w-4 h-4" />
                TikTok Live
              </button>
              {!customer && (
                <button
                  onClick={() => { setActiveTab('signup'); window.scrollTo(0, 0); }}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                    activeTab === 'signup'
                      ? 'border-[#0FA49C] text-[#0FA49C]'
                      : 'border-transparent text-[#0FA49C] hover:text-[#0d8f88]'
                  }`}
                  data-testid="tab-signup"
                >
                  <UserPlus className="w-4 h-4" />
                  Create Account
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <UserMenu onNavigateAccount={() => { setActiveTab('account'); setMobileMenuOpen(false); window.scrollTo(0, 0); }} />
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(v => !v)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl hover:bg-gray-100 text-[#222] transition-colors"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu-panel">
            <div className="max-w-7xl mx-auto px-4 py-2 flex flex-col">
              {[
                { id: 'home', icon: Home, label: 'Home' },
                { id: 'sample-box', icon: Package, label: 'Sample Box' },
                ...(tedAiEnabled ? [{ id: 'ted-ai', icon: Bot, label: 'Ted AI' }] : []),
                { id: 'get-matched', icon: PawPrint, label: 'Dog Match' },
                ...(tikTokLiveEnabled ? [{ id: 'tiktok-live', icon: Sparkles, label: 'TikTok Live' }] : []),
                ...(!customer ? [{ id: 'signup', icon: UserPlus, label: 'Create Account', highlight: true }] : []),
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); window.scrollTo(0, 0); }}
                  className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-colors ${
                    activeTab === item.id
                      ? 'bg-[#0FA49C]/10 text-[#0FA49C]'
                      : item.highlight
                        ? 'text-[#0FA49C] hover:bg-[#0FA49C]/5'
                        : 'text-[#444] hover:bg-gray-50'
                  }`}
                  data-testid={`mobile-tab-${item.id}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {activeTab === 'home' ? (
        <HomePage
          tedAiEnabled={tedAiEnabled}
          onNavigate={(target) => {
            setActiveTab(target);
            window.scrollTo(0, 0);
          }}
        />
      ) : activeTab === 'ted-ai' ? (
        <TedAI onCompleteOrder={() => { setActiveTab('sample-box'); setShowCheckout(true); }} />
      ) : activeTab === 'get-matched' ? (
        <DogMatch onCompleteOrder={() => { setActiveTab('sample-box'); setShowCheckout(true); }} />
      ) : activeTab === 'tiktok-live' ? (
        <TikTokLive />
      ) : activeTab === 'account' ? (
        <AccountPage />
      ) : activeTab === 'signup' ? (
        <SignUpPage
          onSuccess={() => {
            if (cameFromTedsTable) {
              navigate('/teds-table?code=NEW10');
              return;
            }
            setActiveTab('account');
            window.scrollTo(0, 0);
          }}
        />
      ) : (
      <>
      {/* Hero + Trust only on sample-box tab */}
      <SampleBoxHero boxSizes={boxSizes} />
      <TrustSection />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32 lg:pb-8">
        {/* Section Header */}
        <div className="flex items-center gap-3 mb-6">
          <Sparkles className="w-6 h-6 text-[#0FA49C]" />
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#222]">Choose Your Samples</h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Filters & Products */}
          <div className="flex-1 lg:w-[60%]">
            {/* Filters */}
            <div className="mb-6">
              <FilterBar filters={filters} setFilters={setFilters} />
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 h-80 animate-pulse" />
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-[#666] font-medium">No products match your filters</p>
                <button
                  onClick={() => setFilters({ type: null, lifestage: null, breed_size: null })}
                  className="text-[#0FA49C] font-semibold mt-2 hover:underline"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <motion.div 
                layout 
                className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-stretch"
                data-testid="products-grid"
              >
                <AnimatePresence>
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </div>

          {/* Right Column - Box Visualizer (Desktop) */}
          <div className="hidden lg:block lg:w-[40%]">
            <div className="sticky-box">
              <BoxVisualizer onCheckout={handleCheckout} />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Bar */}
      <MobileBottomBar 
        onViewBox={() => setShowBoxDrawer(true)} 
        onCheckout={handleCheckout}
      />

      {/* Mobile Box Drawer */}
      <BoxDrawer 
        open={showBoxDrawer} 
        onClose={() => setShowBoxDrawer(false)}
        onCheckout={handleCheckout}
      />

      {/* Checkout Modal */}
      <CheckoutModal 
        open={showCheckout} 
        onClose={() => setShowCheckout(false)} 
      />
      </>
      )}
    </>
  );
};

function AppRouter() {
  const location = useLocation();

  // Handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Handle password reset
  if (location.hash?.includes('reset_token=')) {
    return <ResetPassword />;
  }

  return (
    <Routes>
      <Route path="/" element={
        <div className="min-h-screen bg-white" data-testid="app-container">
          <MainContent />
        </div>
      } />
      <Route path="/signup" element={
        <div className="min-h-screen bg-white" data-testid="app-container">
          <MainContent initialTab="signup" />
        </div>
      } />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/vs/:slug" element={<PublicVsPage />} />
      <Route path="/teds-table" element={<TedsTablePage />} />
      <Route path="/teds-table/success" element={<TedsTableSuccess />} />
      <Route path="/ffos" element={<FFOSSignUpPage />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BoxProvider>
        <Toaster position="top-center" richColors />
        <AppRouter />
      </BoxProvider>
    </AuthProvider>
  );
}

export default App;
